/**
 * Обработчики next-intl, общие для корневого провайдера и для слоёв сообщений семейств маршрутов
 * (KAN-975): вложенный провайдер не наследует `onError` и `getMessageFallback`, их нужно передавать
 * заново, иначе внутри слоя пропавший ключ бросал бы ошибку вместо запасного текста.
 */

/**
 * Ключи, которые после KAN-1286 держат русские строки транслитом: `Ui.lit<Что‑то>`.
 * Такой огрызок в интерфейсе читается как код элемента (`litEmpty…`), а не как текст.
 */
const TRANSLIT_KEY = /(^|\.)lit[A-Z0-9_]/;

/** Бэкенд-коды, которые всплывали вместо подписи: `draft_in_progress`, `in_progress`. */
const CODE_LIKE_SEGMENT = /^[a-z0-9]+(?:_[a-z0-9]+)+$/i;

/**
 * Graceful fallback для пропущенных i18n-ключей: показываем последний сегмент
 * (например "cta" вместо "Match.createLobby.cta") вместо сырого ключа в UI.
 * Это safety net на случай неполных переводов — лучше пустой/неточный текст,
 * чем «Match.createLobby.cta» торчащий пользователю.
 *
 * Второй слой (после `messages-usage.test.ts`): если последний сегмент — транслит-ключ
 * (`litEmpty…`) или бэкенд-код (`draft_in_progress`), отдаём пустую строку. Пустое место
 * честнее, чем код элемента вместо названия: именно эти огрызки и лезли в интерфейс.
 */
export function intlMessageFallback({
  key,
}: {
  error: unknown;
  key: string;
  namespace?: string;
}) {
  const lastSegment = key.split(".").pop();
  if (!lastSegment) return "";
  if (TRANSLIT_KEY.test(key) || CODE_LIKE_SEGMENT.test(lastSegment)) return "";
  return lastSegment;
}

export function intlOnError(error: { code: string; message?: string }) {
  // В production молча игнорируем missing message errors — у нас уже есть
  // getMessageFallback. В dev — логируем как warn, чтобы видеть, какие ключи
  // отсутствуют.
  if (error.code === "MISSING_MESSAGE") {
    if (process.env["NODE_ENV"] !== "production") {
      console.warn("[i18n] missing message:", error.message);
    }
    return;
  }
  console.error("[i18n] error:", error);
}
